import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useResizableColumns, ResizeHandle, ColumnDivider, useSortableData, SortableHeader } from '../components/shared/tableUtils';

function StatBadge({ label, count, color }) {
  const colors = {
    gray: 'bg-gray-100 text-gray-600',
    yellow: 'bg-yellow-50 text-yellow-700',
    green: 'bg-green-50 text-green-700',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {label}: {count}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: 'bg-green-50 text-green-700 border-green-200',
    pending: 'bg-gray-100 text-gray-600 border-gray-200',
    expired: 'bg-red-50 text-red-700 border-red-200',
  };
  const labels = {
    completed: 'Completed',
    pending: 'Pending',
    expired: 'Expired',
  };
  const style = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${style}`}>
      {labels[status] || status}
    </span>
  );
}

// Lightweight inline toast — replaces window.alert() so success/error
// messages don't block the page or look like a browser dialog.
function Toast({ kind, message, onDismiss }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [message, onDismiss]);
  if (!message) return null;
  const styles = kind === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-green-50 border-green-200 text-green-700';
  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-5 right-5 z-[60] max-w-sm border rounded-lg shadow-md px-4 py-3 text-[13px] ${styles}`}
      style={{ fontFamily: 'Inter' }}
    >
      <div className="flex items-start gap-3">
        <span className="flex-1">{message}</span>
        <button
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="opacity-60 hover:opacity-100 transition"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center border-b border-[#f0f0f0] animate-pulse" style={{ height: 48 }}>
      {[...Array(7)].map((_, i) => (
        <div key={i} className="flex-1 px-3">
          <div className="h-3 bg-gray-200 rounded w-3/4" />
        </div>
      ))}
    </div>
  );
}

export default function Signatures() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const initials = ((user?.user_metadata?.first_name || user?.email?.[0] || 'U')[0] + ((user?.user_metadata?.last_name || '')[0] || '')).toUpperCase();

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mediatorFilter, setMediatorFilter] = useState('');
  const [caseFilter, setCaseFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [mediators, setMediators] = useState([]);
  const [mediatorDropdownOpen, setMediatorDropdownOpen] = useState(false);
  // Search box inside the mediator dropdown — typing narrows the list.
  const [mediatorQuery, setMediatorQuery] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [caseDropdownOpen, setCaseDropdownOpen] = useState(false);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [cases, setCases] = useState([]);
  const [templates, setTemplates] = useState([]);
  // The Create modal flow:
  //   1. CM picks a mediator (scopes the case + template lists below)
  //   2. CM picks a case from THAT mediator's cases
  //   3. CM picks an agreement template from THAT mediator's templates
  //      (defaults to whichever is `is_active=true`, but they can switch)
  //   4. Submit → BE derives signers from the case's clients table, stamps
  //      signing_url per signer, fires per-party email dispatch
  // Signers themselves are NEVER hand-entered — that data already lives on
  // the case's clients/case_parties rows.
  const [newReq, setNewReq] = useState({ mediatorId: '', caseId: '', templateId: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [toast, setToast] = useState({ kind: null, message: null });
  const showToast = (kind, message) => setToast({ kind, message });
  const dismissToast = () => setToast({ kind: null, message: null });

  // Column order + set per Adam's spec: Case Name, Agreement Name,
  // Mediator, Party, Status. Sent/Signed timestamps have been folded
  // away from the list — they live on the detail page now. Keys are
  // versioned to invalidate any stale localStorage widths from the
  // previous order.
  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'signatures.list.v2',
    { case: 220, agreement: 280, mediator: 180, parties: 240, status: 130 }
  );
  const { sortedRows: sortedRequests, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    requests,
    {
      case: (r) => r.caseName || r.case_name || '',
      agreement: (r) => r.agreementName || r.template_name || '',
      mediator: (r) => r.mediatorName || r.mediator_name || '',
      parties: (r) => {
        const names = Array.isArray(r.parties) && r.parties.length
          ? r.parties.map((p) => p.name).filter(Boolean)
          : (r.signers || []).map((s) => s.name).filter(Boolean);
        return names.join(', ');
      },
      status: (r) => r.status || '',
    }
  );

  useEffect(() => {
    api.get('/mediators?limit=200')
      .then(res => setMediators((res.data?.data || []).filter(m => m.name?.trim())))
      .catch(console.error);
    api.get('/cases?page=1&limit=200')
      .then(res => setCases((res.data?.data || []).map(c => ({ id: c.id, name: c.caseName || c.caseNumber, mediatorId: c.mediatorId, mediatorName: c.mediatorName }))))
      .catch(console.error);
    // Templates list for the Create modal — scoped client-side to the
    // selected mediator. Could be moved to a per-mediator BE call if the
    // template count grows, but for now one fetch is fine.
    api.get('/signing/templates')
      .then(res => setTemplates(res.data?.data || []))
      .catch(console.error);
  }, []);

  const fetchData = () => {
    setLoading(true);
    setLoadError(null);
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (mediatorFilter) params.set('mediatorId', mediatorFilter);
    if (caseFilter) params.set('caseId', caseFilter);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    const qs = params.toString();

    Promise.all([
      api.get(`/signing/requests${qs ? `?${qs}` : ''}`),
      // Stats endpoint now respects the same filter params so the KPI
      // badges (pending / completed / etc.) match the visible list.
      api.get(`/signing/stats${qs ? `?${qs}` : ''}`),
    ])
      .then(([reqRes, statsRes]) => {
        setRequests(reqRes.data?.data || reqRes.data || []);
        setStats(statsRes.data?.data || statsRes.data || { pending: 0, completed: 0 });
      })
      .catch(err => {
        console.error(err);
        setLoadError('Could not load signing requests. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  // Debounced 250ms so rapid keystrokes in the search box don't fire
  // one request per keystroke.
  useEffect(() => {
    const handler = setTimeout(fetchData, 250);
    return () => clearTimeout(handler);
  }, [searchQuery, statusFilter, mediatorFilter, caseFilter, dateFrom, dateTo]);

  // Resends the signing link to every party on the case. BE rotates the
  // session token once and fans the email per party using the same CC-
  // vs-fan-out pattern as onboarding emails. For per-party resend
  // (single party only), open the detail page and click the Resend
  // button on that party's row.
  const handleResend = async (e, id) => {
    e.stopPropagation();
    setResendingId(id);
    try {
      const res = await api.post(`/signing/requests/${id}/resend`);
      const status = res.data?.emailDispatchStatus;
      if (status === 'failed') {
        showToast('error', 'All resends failed. Check the BE logs.');
      } else if (status === 'partial') {
        showToast('error', 'Some resends failed. Check the BE logs.');
      } else {
        showToast('success', 'Signing link resent to all parties.');
      }
    } catch (err) {
      console.error(err);
      showToast('error', err.response?.data?.message || 'Failed to resend signing links.');
    } finally {
      setResendingId(null);
    }
  };

  // CM-controlled lifecycle: status is never auto-flipped on submit. The
  // case manager marks the request complete (or reopens it) from here.
  const handleStatusChange = async (e, id, nextStatus) => {
    e.stopPropagation();
    setUpdatingStatusId(id);
    try {
      await api.patch(`/signing/requests/${id}/status`, { status: nextStatus });
      showToast('success', `Marked as ${nextStatus.replace('_', ' ')}.`);
      fetchData();
    } catch (err) {
      console.error(err);
      showToast('error', err.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDownload = async (e, id) => {
    e.stopPropagation();
    setDownloadingId(id);
    try {
      const res = await api.get(`/signing/requests/${id}/download`, { responseType: 'blob' });
      // Endpoint returns a zip with two PDFs (agreement + Cert of
      // Completion). Use the BE-supplied filename when present so the
      // case name is in the filename; otherwise fall back to a
      // generated .zip (NOT .pdf — corrupt files from a wrong ext).
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = (match && match[1]) || `signing-bundle-${id}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to download signing bundle.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Per SOW Section 04 Step 6C: Status filter must include Pending,
  // Partially Signed, Completed, Expired.
  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' },
    { label: 'Expired', value: 'expired' },
  ];

  const activeFilterCount = [
    statusFilter !== 'all',
    !!mediatorFilter,
    !!caseFilter,
    !!(dateFrom || dateTo),
    !!searchQuery,
  ].filter(Boolean).length;
  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setMediatorFilter('');
    setCaseFilter('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="flex flex-col bg-white min-h-screen">
      {/* Header Bar — wraps to two rows on small screens. Stat badges
          can wrap independently of the title. Action buttons keep
          their layout on the right and shrink with whitespace-nowrap. */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border-b border-[#e5e5e5] px-4 sm:px-6 py-3">
        <div className="flex items-center flex-wrap gap-3">
          <span className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
            Signatures
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <StatBadge label="Pending" count={stats.pending || 0} color="gray" />
            <StatBadge label="Completed" count={stats.completed || 0} color="green" />
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={() => navigate('/signatures/templates')}
            className="flex items-center gap-2 h-8 px-3 rounded-lg border border-[#e5e5e5] bg-white hover:bg-gray-50 text-[13px] font-medium text-[#0e1e20] whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            Manage Templates
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 h-8 px-4 rounded-lg bg-[#115e59] text-white text-[13px] font-medium hover:bg-[#0d4f4a] whitespace-nowrap"
            style={{ fontFamily: 'Inter' }}
          >
            + New Request
          </button>
          <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Inter' }}>{initials}</span>
          </div>
        </div>
      </div>

      {/* Filters Row — wraps on narrow screens. Search input is full
          width on mobile, fixed 300px on tablet+. Filter dropdowns
          flex-wrap so they stack as needed. */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-[#f0f0f0]">
        <div className="relative w-full sm:w-[300px]">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by agreement, case, or party..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#e5e5e5] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
            style={{ fontFamily: 'Inter' }}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Mediator dropdown */}
          <div className="relative">
            <button
              onClick={() => { setMediatorDropdownOpen(!mediatorDropdownOpen); setStatusDropdownOpen(false); setCaseDropdownOpen(false); setDateDropdownOpen(false); }}
              aria-haspopup="listbox"
              aria-expanded={mediatorDropdownOpen}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${mediatorFilter ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
            >
              <span className="text-[13px] font-medium text-[#0e1e20] max-w-[150px] truncate" style={{ fontFamily: 'Inter' }}>
                {mediatorFilter ? mediators.find(m => m.userId === mediatorFilter)?.name || 'Mediator' : 'Mediator'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {mediatorDropdownOpen && (() => {
              const q = mediatorQuery.trim().toLowerCase();
              const filtered = q ? mediators.filter(m => (m.name || '').toLowerCase().includes(q)) : mediators;
              return (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50 flex flex-col" style={{ maxHeight: 320 }}>
                  <div className="p-2 border-b border-[#e5e5e5]">
                    <div className="relative">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                        <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search mediators..."
                        value={mediatorQuery}
                        onChange={(e) => setMediatorQuery(e.target.value)}
                        autoFocus
                        className="w-full h-8 pl-8 pr-2 rounded-md border border-[#e5e5e5] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                        style={{ fontFamily: 'Inter' }}
                      />
                    </div>
                  </div>
                  <div className="overflow-auto flex-1">
                    <button
                      onClick={() => { setMediatorFilter(''); setMediatorDropdownOpen(false); setMediatorQuery(''); }}
                      className={`w-full text-left px-3 py-2 text-[13px] font-['Inter'] hover:bg-[#f5f5f5] ${!mediatorFilter ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
                    >
                      All Mediators
                    </button>
                    {filtered.length === 0 && (
                      <div className="px-3 py-3 text-[12px] text-[#9ca3af] font-['Inter']">No mediators found</div>
                    )}
                    {filtered.map(m => (
                      <button
                        key={m.id}
                        onClick={() => { setMediatorFilter(m.userId); setMediatorDropdownOpen(false); setMediatorQuery(''); }}
                        className={`w-full text-left px-3 py-2 text-[13px] font-['Inter'] hover:bg-[#f5f5f5] ${mediatorFilter === m.userId ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Case dropdown */}
          <div className="relative">
            <button
              onClick={() => { setCaseDropdownOpen(!caseDropdownOpen); setMediatorDropdownOpen(false); setStatusDropdownOpen(false); setDateDropdownOpen(false); }}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${caseFilter ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
              aria-haspopup="listbox"
              aria-expanded={caseDropdownOpen}
            >
              <span className="text-[13px] font-medium text-[#0e1e20] max-w-[140px] truncate" style={{ fontFamily: 'Inter' }}>
                {caseFilter ? (cases.find(c => String(c.id) === String(caseFilter))?.name || 'Case') : 'Case'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {caseDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 w-[260px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50 max-h-[280px] overflow-auto">
                <button
                  onClick={() => { setCaseFilter(''); setCaseDropdownOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-[13px] font-['Inter'] hover:bg-[#f5f5f5] ${!caseFilter ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
                >
                  All Cases
                </button>
                {cases.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setCaseFilter(String(c.id)); setCaseDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-[13px] font-['Inter'] hover:bg-[#f5f5f5] ${String(caseFilter) === String(c.id) ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Range filter (per SOW Section 04 Step 6C) */}
          <div className="relative">
            <button
              onClick={() => { setDateDropdownOpen(!dateDropdownOpen); setMediatorDropdownOpen(false); setStatusDropdownOpen(false); setCaseDropdownOpen(false); }}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${(dateFrom || dateTo) ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
              aria-haspopup="dialog"
              aria-expanded={dateDropdownOpen}
            >
              <span className="text-[13px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>
                {dateFrom || dateTo ? `${dateFrom || '…'} → ${dateTo || '…'}` : 'Date Range'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {dateDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 w-[260px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50 p-3" role="dialog" aria-label="Date range filter">
                <label className="block text-[11px] font-medium text-[#5b5a64] mb-1" style={{ fontFamily: 'Inter' }}>From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full h-8 px-2 mb-2 rounded-md border border-[#e5e5e5] text-[13px] outline-none focus:border-[#115e59]"
                />
                <label className="block text-[11px] font-medium text-[#5b5a64] mb-1" style={{ fontFamily: 'Inter' }}>To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom || undefined}
                  className="w-full h-8 px-2 rounded-md border border-[#e5e5e5] text-[13px] outline-none focus:border-[#115e59]"
                />
                <div className="flex justify-between items-center mt-3">
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-[12px] text-[#5b5a64] hover:text-[#0e1e20]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setDateDropdownOpen(false)}
                    className="px-3 h-7 text-[12px] rounded-md bg-[#115e59] text-white"
                    style={{ fontFamily: 'Inter' }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Status dropdown */}
          <div className="relative">
            <button
              onClick={() => { setStatusDropdownOpen(!statusDropdownOpen); setMediatorDropdownOpen(false); setCaseDropdownOpen(false); setDateDropdownOpen(false); }}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${statusFilter !== 'all' ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
              aria-haspopup="listbox"
              aria-expanded={statusDropdownOpen}
            >
              <span className="text-[13px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>
                {statusOptions.find(o => o.value === statusFilter)?.label || 'All Statuses'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {statusDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 w-[200px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50">
                {statusOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setStatusFilter(opt.value); setStatusDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-[13px] font-['Inter'] hover:bg-[#f5f5f5] ${statusFilter === opt.value ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear-all chip — only visible when filters are active */}
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-[12px] text-[#5b5a64] hover:text-[#0e1e20] underline"
              style={{ fontFamily: 'Inter' }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="rounded-xl bg-white border border-[#f0f0f0] overflow-x-auto">
          {/* Min-width keeps the columns from squashing to unreadable
              widths on narrow viewports — horizontal scroll kicks in
              instead. The Actions column was clipping "Mark Complete"
              at 140px; bumped to a min-width that fits all 3 buttons. */}
          <div className="min-w-[1100px]">
          {/* Table Header — final column order per Adam's spec:
              Case Name → Agreement Name → Mediator → Party → Status →
              Actions. The previous Sent / Signed column has been
              removed; that information lives on the detail page. */}
          <div className="flex items-center bg-[#f9fafb] border-b border-[#f0f0f0]" style={{ height: 45 }}>
            <div className="flex items-center h-full px-4 relative min-w-0" style={{ width: colWidths.case }}>
              <SortableHeader sortKey="case" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Case Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('case')} getCurrentWidth={() => colWidths.case} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.agreement }}>
              <SortableHeader sortKey="agreement" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Agreement Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('agreement')} getCurrentWidth={() => colWidths.agreement} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.mediator }}>
              <SortableHeader sortKey="mediator" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Mediator</SortableHeader>
              <ResizeHandle onResize={setColWidth('mediator')} getCurrentWidth={() => colWidths.mediator} />
            </div>
            <div className="flex items-center h-full px-3 relative min-w-0" style={{ width: colWidths.parties }}>
              <SortableHeader sortKey="parties" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Party</SortableHeader>
              <ResizeHandle onResize={setColWidth('parties')} getCurrentWidth={() => colWidths.parties} />
            </div>
            <div className="flex-1 flex items-center h-full px-3 min-w-0">
              <SortableHeader sortKey="status" sort={tableSort} onSort={requestTableSort} className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Status</SortableHeader>
            </div>
            <div className="flex items-center justify-end h-full px-4 shrink-0" style={{ width: 280 }}>
              <span className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>Actions</span>
            </div>
          </div>

          {/* Loading skeleton */}
          {loading && (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          )}

          {/* Error state */}
          {!loading && loadError && (
            <div className="flex flex-col items-center justify-center py-16" role="alert">
              <svg className="w-12 h-12 text-red-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>{loadError}</span>
              <button
                onClick={fetchData}
                className="mt-3 text-xs font-medium text-[#115e59] hover:underline"
                style={{ fontFamily: 'Inter' }}
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty state — differentiates "no data ever" from "filtered out". */}
          {!loading && !loadError && requests.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              {activeFilterCount > 0 ? (
                <>
                  <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>No requests match the current filters</span>
                  <button
                    onClick={clearAllFilters}
                    className="text-xs font-medium text-[#115e59] hover:underline mt-2"
                    style={{ fontFamily: 'Inter' }}
                  >
                    Clear all filters
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>No signing requests yet</span>
                  <span className="text-xs text-gray-400 mt-1" style={{ fontFamily: 'Inter' }}>Signing requests will appear here once created from cases.</span>
                </>
              )}
            </div>
          )}

          {/* Rows — column order mirrors the header above: Case Name,
              Agreement Name, Mediator, Party, Status, then Actions. */}
          {!loading && !loadError && sortedRequests.map((req) => (
            <div
              key={req.id}
              className="flex items-center border-b border-[#f0f0f0] cursor-pointer hover:bg-gray-50 transition-colors"
              style={{ height: 48 }}
              onClick={() => navigate(`/signatures/${req.id}`)}
            >
              <div className="flex items-center h-full px-4 min-w-0" style={{ width: colWidths.case }}>
                <span className="text-[13px] font-medium text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>
                  {req.caseName || req.case_name || '-'}
                </span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.agreement }}>
                <span className="text-[13px] text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>
                  {req.agreementName || req.template_name || 'Untitled Agreement'}
                </span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.mediator }}>
                <span className="text-[13px] text-[#0e1e20] truncate" style={{ fontFamily: 'Inter' }}>
                  {req.mediatorName || req.mediator_name || '-'}
                </span>
              </div>
              <div className="flex items-center h-full px-3 min-w-0" style={{ width: colWidths.parties }}>
                <span className="text-[13px] text-[#5b5a64] truncate" style={{ fontFamily: 'Inter' }}>
                  {(() => {
                    const names = Array.isArray(req.parties) && req.parties.length
                      ? req.parties.map((p) => p.name).filter(Boolean)
                      : (req.signers || []).map((s) => s.name).filter(Boolean);
                    return names.length ? names.join(', ') : '-';
                  })()}
                </span>
              </div>
              <div className="flex-1 flex items-center h-full px-3 min-w-0">
                <StatusBadge status={req.status} />
              </div>
              <div className="flex items-center justify-end gap-1.5 h-full px-4 shrink-0" style={{ width: 280 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/signatures/${req.id}`); }}
                  className="px-2.5 py-1 text-[11px] font-medium text-[#115e59] bg-[#f0fdfa] rounded hover:bg-[#ccfbf1] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'Inter' }}
                >
                  View
                </button>
                {req.status !== 'completed' && (
                  <button
                    onClick={(e) => handleResend(e, req.id)}
                    disabled={resendingId === req.id}
                    className="px-2.5 py-1 text-[11px] font-medium text-[#5b5a64] bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    style={{ fontFamily: 'Inter' }}
                  >
                    {resendingId === req.id ? 'Sending…' : 'Resend'}
                  </button>
                )}
                {req.status !== 'completed' ? (
                  <button
                    onClick={(e) => handleStatusChange(e, req.id, 'completed')}
                    disabled={updatingStatusId === req.id}
                    title="Mark this signing request as complete"
                    className="px-2.5 py-1 text-[11px] font-medium text-[#065f46] bg-[#d1fae5] rounded hover:bg-[#a7f3d0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    style={{ fontFamily: 'Inter' }}
                  >
                    {updatingStatusId === req.id ? '…' : 'Mark Complete'}
                  </button>
                ) : (
                  <button
                    onClick={(e) => handleStatusChange(e, req.id, 'pending')}
                    disabled={updatingStatusId === req.id}
                    title="Reopen — flip back to pending"
                    className="px-2.5 py-1 text-[11px] font-medium text-[#5b5a64] bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    style={{ fontFamily: 'Inter' }}
                  >
                    {updatingStatusId === req.id ? '…' : 'Reopen'}
                  </button>
                )}
              </div>
            </div>
          ))}
          </div>{/* /min-w-[1100px] */}
        </div>
      </div>

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl w-[520px] max-h-[90vh] overflow-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 style={{ fontFamily: '"Inter Tight", sans-serif', fontSize: 18, fontWeight: 700, color: '#111827' }}>Create Signing Request</h3>
              <button onClick={() => setShowCreateModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </div>
            <div className="px-6 pb-2 flex flex-col gap-4">
              {(() => {
                // Cascading dropdowns: mediator scopes both case + template
                // lists. Cases are filtered by mediator.userId (the FK
                // cases.mediatorId joins to). Templates are filtered by
                // agreement_templates.mediator_id which holds the same
                // mediator user_id string.
                const filteredCases = newReq.mediatorId
                  ? cases.filter(c => c.mediatorId === newReq.mediatorId)
                  : [];
                const filteredTemplates = newReq.mediatorId
                  ? templates.filter(t => t.mediator_id === newReq.mediatorId)
                  : [];
                const activeTemplateId = filteredTemplates.find(t => t.is_active)?.id;
                return (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-semibold text-gray-700">Mediator</label>
                      <select
                        value={newReq.mediatorId}
                        onChange={e => {
                          // Changing mediator clears any previously-picked
                          // case and template since both are now scoped.
                          const mid = e.target.value;
                          const newActive = mid
                            ? templates.find(t => t.mediator_id === mid && t.is_active)?.id || ''
                            : '';
                          setNewReq({ mediatorId: mid, caseId: '', templateId: newActive ? String(newActive) : '' });
                        }}
                        className="h-10 px-3 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#115e59]"
                      >
                        <option value="">Select mediator...</option>
                        {mediators.map(m => (
                          <option key={m.id} value={m.userId}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-semibold text-gray-700">Case</label>
                      <select
                        value={newReq.caseId}
                        onChange={e => setNewReq(p => ({ ...p, caseId: e.target.value }))}
                        disabled={!newReq.mediatorId}
                        className="h-10 px-3 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#115e59] disabled:bg-gray-50 disabled:text-gray-400"
                      >
                        <option value="">
                          {!newReq.mediatorId
                            ? 'Select a mediator first'
                            : filteredCases.length === 0
                            ? 'No cases for this mediator'
                            : 'Select case...'}
                        </option>
                        {filteredCases.map(c => (
                          <option key={c.id} value={c.id}>{c.name} (#{c.id})</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[13px] font-semibold text-gray-700">Agreement Template</label>
                      <select
                        value={newReq.templateId}
                        onChange={e => setNewReq(p => ({ ...p, templateId: e.target.value }))}
                        disabled={!newReq.mediatorId}
                        className="h-10 px-3 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#115e59] disabled:bg-gray-50 disabled:text-gray-400"
                      >
                        <option value="">
                          {!newReq.mediatorId
                            ? 'Select a mediator first'
                            : filteredTemplates.length === 0
                            ? 'No templates for this mediator'
                            : 'Select template...'}
                        </option>
                        {filteredTemplates.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.template_name}{t.id === activeTemplateId ? ' (active)' : ''}
                          </option>
                        ))}
                      </select>
                      {newReq.mediatorId && filteredTemplates.length === 0 && (
                        <span className="text-[11px] text-amber-700">
                          This mediator has no agreement templates yet. Upload one from the Templates tab.
                        </span>
                      )}
                    </div>
                    <div className="px-3 py-2.5 rounded-lg bg-[#f8fafc] border border-[#e5e7eb] text-[12px] text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter' }}>
                      One signing link will be generated per party on the case (plaintiffs, defendants, and any additional clients), pre-filled from each client's contact info. Each party receives an email with their personal signing link.
                    </div>
                  </>
                );
              })()}
              {createError && (
                <div role="alert" className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-[12px] text-red-700" style={{ fontFamily: 'Inter' }}>
                  {createError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-5">
              <button onClick={() => { setShowCreateModal(false); setCreateError(null); }} className="h-9 px-5 rounded-lg border border-gray-200 text-sm font-medium text-gray-500 hover:bg-gray-50">Cancel</button>
              <button
                onClick={async () => {
                  if (!newReq.caseId || !newReq.templateId) return;
                  setCreating(true);
                  setCreateError(null);
                  try {
                    const resp = await api.post('/signing/requests', {
                      caseId: parseInt(newReq.caseId, 10),
                      templateId: newReq.templateId,
                    });
                    const reason = resp.data?.reason;
                    const dispatchStatus = resp.data?.emailDispatchStatus;
                    const detail = resp.data?.emailDispatchDetail;
                    const sentCount = Array.isArray(detail?.sent_to) ? detail.sent_to.length : null;
                    setShowCreateModal(false);
                    setNewReq({ mediatorId: '', caseId: '', templateId: '' });
                    let msg;
                    if (reason === 'already_exists') {
                      msg = dispatchStatus === 'dispatched' && sentCount !== null
                        ? `Signing request already existed for this case — re-sent emails to ${sentCount} recipient(s).`
                        : 'A signing request already exists for this case.';
                    } else if (dispatchStatus === 'dispatched') {
                      msg = sentCount !== null
                        ? `Signing request created — emails sent to ${sentCount} recipient(s).`
                        : 'Signing request created and emails dispatched.';
                    } else if (dispatchStatus === 'failed') {
                      msg = 'Signing request created, but email dispatch failed. You can resend from the row.';
                    } else {
                      msg = 'Signing request created. Email dispatch was skipped (SCALE_BE_URL not configured).';
                    }
                    showToast(dispatchStatus === 'failed' ? 'error' : 'success', msg);
                    fetchData();
                  } catch (err) {
                    console.error(err);
                    setCreateError(err.response?.data?.message || 'Failed to create signing request.');
                  }
                  finally { setCreating(false); }
                }}
                disabled={creating || !newReq.caseId || !newReq.templateId}
                className="h-9 px-5 rounded-lg text-sm font-semibold text-white"
                style={{ backgroundColor: creating || !newReq.caseId || !newReq.templateId ? '#94a3b8' : '#115e59' }}
              >
                {creating ? 'Creating...' : 'Create & Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast kind={toast.kind} message={toast.message} onDismiss={dismissToast} />
    </div>
  );
}
